import api from '@/shared/utils/apiConfig';
import type { ApiResponse } from '@/shared/types/api';
import type { PaymentMethodIconToken } from '@/modules/payment/utils/paymentMethodIcon';

export type PaymentMethodUsageContext =
  | 'payment'
  | 'income'
  | 'expense'
  | 'commission'
  | 'wallet_funding'
  | 'filter';

export interface PaymentMethodActor {
  id: string;
  name: string;
}

export interface PaymentMethodCatalogItem {
  id: string;
  name: string;
  code: string;
  isSystem: boolean;
  isActive: boolean;
  sortOrder: number;
  icon?: PaymentMethodIconToken;
  createdBy?: PaymentMethodActor;
  updatedBy?: PaymentMethodActor;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreatePaymentMethodPayload {
  name: string;
  sortOrder?: number;
  icon?: PaymentMethodIconToken;
}

export interface UpdatePaymentMethodPayload {
  name?: string;
  isActive?: boolean;
  sortOrder?: number;
  icon?: PaymentMethodIconToken;
}

export interface PaymentMethodListParams {
  includeInactive?: boolean;
  context?: PaymentMethodUsageContext;
  search?: string;
  page?: number;
  limit?: number;
}

export interface PaymentMethodListResult {
  items: PaymentMethodCatalogItem[];
  total: number;
}

const buildListParams = (params: PaymentMethodListParams = {}) => ({
  includeInactive: params.includeInactive ? 'true' : undefined,
  context: params.context,
  search: params.search?.trim() || undefined,
  page: params.page,
  limit: params.limit,
});

export const paymentMethodAPI = {
  listResult: async (
    params: PaymentMethodListParams = {}
  ): Promise<PaymentMethodListResult> => {
    const response = await api.get<ApiResponse<PaymentMethodCatalogItem[]>>('/payment-methods', {
      params: buildListParams(params),
    });
    const items = response.data.data ?? [];
    return {
      items,
      total: response.data.total ?? items.length,
    };
  },

  getAll: async (options: {
    includeInactive?: boolean;
    context?: PaymentMethodUsageContext;
  } = {}): Promise<ApiResponse<PaymentMethodCatalogItem[]>> => {
    const params: Record<string, string> = {};
    if (options.includeInactive) params.includeInactive = 'true';
    if (options.context) params.context = options.context;
    const response = await api.get('/payment-methods', { params });
    return response.data;
  },

  create: async (
    payload: CreatePaymentMethodPayload
  ): Promise<ApiResponse<PaymentMethodCatalogItem>> => {
    const response = await api.post('/payment-methods', payload);
    return response.data;
  },

  update: async (
    id: string,
    payload: UpdatePaymentMethodPayload
  ): Promise<ApiResponse<PaymentMethodCatalogItem>> => {
    const response = await api.put(`/payment-methods/${id}`, payload);
    return response.data;
  },

  deactivate: async (id: string): Promise<ApiResponse<PaymentMethodCatalogItem>> => {
    const response = await api.delete(`/payment-methods/${id}`);
    return response.data;
  },
};
